/**
 * OpeningToolbar — the controls for the selected window or door.
 *
 * What it is, which style it is drawn in, quick sizes for garage doors (the same
 * list the wizard's garage step offers), duplicate / arrange / delete, and its
 * exact position and size.
 * A door or garage door stands on the floor, so its numbers never lift it —
 * until its "On floor" switch is turned off, which frees that one door to be
 * moved up and down like a window (it can still come down to the floor).
 *
 * Purely presentational, like SectionToolbar.
 */

import React from 'react';
import { Copy, FlipHorizontal2, Pin, PinOff, Repeat, Trash2 } from 'lucide-react';
import { DoorHinge, DoorSwing, Opening, Surface, doorHinge, doorSwing, openingOnFloor, openingSitsOnFloor } from '../../types/sections';
import { MIN_OPENING_SIZE_FT } from '../../calculations/sections';
import { GARAGE_DOOR_PRESETS } from '../../data/presets';
import { OPENING_STYLES, openingStyle } from '../ui/room3d/openingStyles';
import { RectFields, RectField, RectPatch } from './RectFields';
import { chipStyle, iconChipStyle, toolbarDivider } from './SectionToolbar';
import { DESIGNER_STRINGS as S } from './strings';

export interface OpeningToolbarProps {
  opening: Opening;
  surface: Surface;
  xFlipped: boolean;
  /** Safe margin, feet. */
  margin: number;
  onRect: (patch: RectPatch, field: RectField) => void;
  /** A style of this opening's kind was chosen (an id from openingStyles.ts). */
  onStyle: (styleId: string) => void;
  /** Doors and garage doors: pin this one to the floor, or free it. */
  onFloorPinned: (pinned: boolean) => void;
  /** Doors: which side the hinges are on, and which way it opens. */
  onHanding?: (patch: { hinge?: DoorHinge; swing?: DoorSwing }) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  actions?: React.ReactNode;
}

const selectStyle: React.CSSProperties = {
  height: 30, borderRadius: 7, padding: '0 8px', fontSize: 12, fontWeight: 600, color: '#fff',
  background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.25)',
};

export const OpeningToolbar: React.FC<OpeningToolbarProps> = ({
  opening,
  surface,
  xFlipped,
  margin,
  onRect,
  onStyle,
  onFloorPinned,
  onHanding,
  onDuplicate,
  onDelete,
  actions,
}) => {
  // Where a shared wall has been taken out: nothing here to edit.
  if (opening.kind === 'passage') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#fff', maxWidth: 520 }}>
        <span style={{ ...chipStyle(true), display: 'flex', alignItems: 'center', cursor: 'default' }}>{S.openingName.passage}</span>
        <span style={{ fontSize: 12, color: '#c9cbd3', lineHeight: 1.4 }}>{S.planner.wallRemoved}</span>
      </div>
    );
  }
  const onFloor = openingOnFloor(opening);
  const preset = GARAGE_DOOR_PRESETS.find((p) => Math.abs(p.width - opening.width) < 1e-6 && Math.abs(p.height - opening.height) < 1e-6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, color: '#fff', userSelect: 'none', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <span style={{ ...chipStyle(true), display: 'flex', alignItems: 'center', cursor: 'default' }}>{S.openingName[opening.kind]}</span>

        {opening.kind === 'garageDoor' && (
          <select
            aria-label={S.openingSize}
            title={S.openingSize}
            value={preset ? `${preset.width}x${preset.height}` : ''}
            onChange={(e) => {
              const p = GARAGE_DOOR_PRESETS.find((g) => `${g.width}x${g.height}` === e.target.value);
              if (p) onRect({ width: p.width, height: p.height }, 'width');
            }}
            style={selectStyle}
          >
            {!preset && <option value="" style={{ color: '#000' }}>{S.openingCustomSize}</option>}
            {GARAGE_DOOR_PRESETS.map((p) => (
              <option key={`${p.width}x${p.height}`} value={`${p.width}x${p.height}`} style={{ color: '#000' }}>{p.label}</option>
            ))}
          </select>
        )}

        <select
          aria-label={S.openingStyle}
          title={S.openingStyle}
          value={openingStyle(opening.kind, opening.style).id}
          onChange={(e) => onStyle(e.target.value)}
          style={selectStyle}
        >
          {OPENING_STYLES[opening.kind].map((s) => (
            <option key={s.id} value={s.id} style={{ color: '#000' }}>{S.openingStyleName[s.id] ?? s.id}</option>
          ))}
        </select>

        {openingSitsOnFloor(opening.kind) && (
          <button
            type="button"
            aria-pressed={onFloor}
            title={onFloor ? S.floorPinnedOn : S.floorPinnedOff}
            onClick={() => onFloorPinned(!onFloor)}
            style={{ ...chipStyle(onFloor), display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {onFloor ? <Pin size={14} /> : <PinOff size={14} />} {S.floorPinned}
          </button>
        )}

        {opening.kind === 'door' && onHanding && (
          <>
            <button
              type="button"
              title={S.hingeTitle}
              onClick={() => onHanding({ hinge: doorHinge(opening) === 'left' ? 'right' : 'left' })}
              style={{ ...chipStyle(false), display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FlipHorizontal2 size={14} /> {S.hinge[doorHinge(opening)]}
            </button>
            <button
              type="button"
              title={S.swingTitle}
              onClick={() => onHanding({ swing: doorSwing(opening) === 'in' ? 'out' : 'in' })}
              style={{ ...chipStyle(false), display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Repeat size={14} /> {S.swing[doorSwing(opening)]}
            </button>
          </>
        )}

        {toolbarDivider}
        <button type="button" title={S.duplicate} aria-label={S.duplicate} onClick={onDuplicate} style={iconChipStyle}>
          <Copy size={15} />
        </button>
        {actions}
        <button type="button" title={S.deleteOpening} aria-label={S.deleteOpening} onClick={onDelete} style={iconChipStyle}>
          <Trash2 size={15} />
        </button>
      </div>

      <RectFields
        rect={opening}
        surface={surface}
        xFlipped={xFlipped}
        minSize={MIN_OPENING_SIZE_FT}
        inset={margin}
        bottomInset={openingSitsOnFloor(opening.kind) ? 0 : margin}
        onFloor={onFloor}
        onRect={onRect}
      />
    </div>
  );
};

export default OpeningToolbar;
